import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { handleApiError } from '@/lib/utils'
import { 
  Users, 
  Briefcase, 
  Calendar, 
  TrendingUp, 
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye,
  Plus,
  Filter,
  Download,
  Star,
  UserCheck,
  MessageSquare,
  BarChart3,
  Loader,
  ArrowRight
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import InterviewScheduleModal from '@/components/interview/InterviewScheduleModal'
import ActivityFeed from '@/components/dashboard/ActivityFeed'
import FavoritePositions from '@/components/dashboard/FavoritePositions'

interface DashboardStats {
  activePositions: number
  totalCandidates: number
  totalInterviewed: number
  totalHired: number
  followedCandidatesCount?: number
  candidatePoolSize?: number
  periodStats: {
    newApplications: number
    interviewsCompleted: number
    hiresMade: number
    averageScore: number
  }
  trends: {
    applications: {
      current: number
      previous: number
      change: number
      percentage: number
    }
  }
  upcomingInterviews: any[]
  topPositions: any[]
  recentActivity: any[]
  activityFeed: any[]
  intelligentInsights: any[]
  favoritePositions: any[]
  urgentActions: any[]
}

interface Position {
  id: string
  title: string
  department: string
  status: 'active' | 'paused' | 'filled'
  candidates: {
    total: number
    interviewed: number
    hired: number
  }
  datePosted: string
  priority: 'high' | 'medium' | 'low'
}

interface Candidate {
  id: string
  name: string
  position: string
  positionId: string
  status: 'pending' | 'interviewed' | 'passed' | 'rejected'
  overallScore: number
  interviewDate?: string
  avatar?: string
  skills: string[]
  aiScore: number
  technicalScore: number
  behavioralScore: number
  isFollowing?: boolean
}

export default function RecruiterDashboard() {
  const navigate = useNavigate()
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null)
  const [positions, setPositions] = useState<Position[]>([])
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showInterviewModal, setShowInterviewModal] = useState(false)
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | undefined>()
  const [selectedPositionId, setSelectedPositionId] = useState<string | undefined>()
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'paused':
        return 'bg-yellow-100 text-yellow-800'
      case 'filled':
        return 'bg-blue-100 text-blue-800'
      case 'passed':
        return 'bg-green-100 text-green-800'
      case 'interviewed':
        return 'bg-blue-100 text-blue-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600'
      case 'medium':
        return 'text-yellow-600'
      case 'low':
        return 'text-green-600'
      default:
        return 'text-gray-600'
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50'
    if (score >= 60) return 'text-yellow-600 bg-yellow-50'
    return 'text-red-600 bg-red-50'
  }

  useEffect(() => {
    loadDashboardData()
  }, [])

  // Real-time event tracking with auto-refresh
  useEffect(() => {
    if (!loading) {
      const interval = setInterval(() => {
        loadDashboardData()
      }, 30000) // Refresh every 30 seconds

      return () => clearInterval(interval)
    }
  }, [loading])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [statsResponse, positionsResponse, candidatesResponse] = await Promise.all([
        apiClient.getDashboardStats(),
        apiClient.getPositions({ limit: 10 }),
        apiClient.getCandidates({ sortBy: 'appliedDate', sortOrder: 'desc' })
      ])

      const candidateResponseData = candidatesResponse.success && candidatesResponse.data
        ? ((candidatesResponse.data as any).candidates || candidatesResponse.data)
        : []

      const processedCandidates = Array.isArray(candidateResponseData)
        ? candidateResponseData.map(candidate => {
            const normalizedFollowing = typeof candidate.isFollowing !== 'undefined'
              ? Boolean(candidate.isFollowing)
              : (typeof candidate.is_following !== 'undefined'
                  ? Boolean(candidate.is_following)
                  : false)

            return {
              ...candidate,
              skills: candidate.skills || [],
              positionId: candidate.positionId || '1',
              status: candidate.status || 'pending',
              overallScore: candidate.best_score || candidate.overallScore || 0,
              interviewDate: candidate.interviewDate || undefined,
              avatar: candidate.avatar || undefined,
              aiScore: candidate.aiScore || candidate.best_score || 0,
              technicalScore: candidate.technicalScore || candidate.best_score || 0,
              behavioralScore: candidate.behavioralScore || candidate.best_score || 0,
              isFollowing: normalizedFollowing
            }
          })
        : []

      const totalCandidateCount = processedCandidates.length
      const followedCandidateCount = processedCandidates.filter(candidate => candidate.isFollowing).length
      setCandidates(processedCandidates.slice(0, 10))

      if (!statsResponse.success || !statsResponse.data) {
        throw new Error('Dashboard stats unavailable')
      }

      const nextDashboardStats = statsResponse.data as DashboardStats

      // Use positions from dashboard stats if available, otherwise API data
      if (statsResponse.success && statsResponse.data?.topPositions?.length > 0) {
        setPositions(statsResponse.data.topPositions.map((position: any) => ({
          ...position,
          datePosted: position.datePosted || new Date().toISOString().split('T')[0]
        })))
      } else if (positionsResponse.success && positionsResponse.data) {
        // Extract positions array from the API response data
        const positionsData = (positionsResponse.data as any).positions || positionsResponse.data
        const processedPositions = Array.isArray(positionsData) ? positionsData.map(position => ({
          ...position,
          id: position.id || position.uuid,
          applicationCount: position.application_count || position.applicationCount || 0,
          createdAt: position.created_at || position.datePosted || position.createdAt || new Date().toISOString(),
          candidates: {
            total: position.application_count || 0,
            interviewed: Math.floor((position.application_count || 0) * 0.4),
            hired: Math.floor((position.application_count || 0) * 0.1)
          },
          datePosted: position.datePosted || new Date().toISOString().split('T')[0],
          priority: position.priority || 'medium'
        })) : []
        setPositions(processedPositions)
      } else {
        setPositions([])
      }

      setDashboardStats({
        ...nextDashboardStats,
        totalCandidates: followedCandidateCount,
        followedCandidatesCount: followedCandidateCount,
        candidatePoolSize: totalCandidateCount
      })

    } catch (err) {
      console.error('Failed to load dashboard data:', err)
      setError('Unable to load dashboard data. Please try again.')
      setDashboardStats(null)
      setPositions([])
      setCandidates([])
    } finally {
      setLoading(false)
      setLastUpdated(new Date())
    }
  }

  const handleScheduleInterview = async () => {
    // This will be handled by the layout's modal  
    console.log('Schedule interview clicked')
  }


  const handleViewPosition = (positionId: string) => {
    window.location.href = `/recruiter/positions/${positionId}`
  }

  const handleViewCandidates = (positionId: string) => {
    window.location.href = `/recruiter/positions/${positionId}/candidates`
  }

  const handleViewAnalytics = (positionId: string) => {
    window.location.href = `/recruiter/positions/${positionId}/analytics`
  }

  const handleViewCandidate = (candidateId: string) => {
    window.location.href = `/recruiter/candidates/${candidateId}`
  }

  const handleMessageCandidate = async (candidateId: string) => {
    // In a real app, this would open a messaging interface
    console.log('Message candidate:', candidateId)
    alert('Messaging feature would open here!')
  }

  const handleQuickSchedule = (candidateId: string, positionId: string) => {
    setSelectedCandidateId(candidateId)
    setSelectedPositionId(positionId)
    setShowInterviewModal(true)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Dashboard</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={loadDashboardData} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!dashboardStats) {
    return null
  }

  const followedCandidatesCount = dashboardStats.followedCandidatesCount ?? dashboardStats.totalCandidates ?? 0

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      {/* Enhanced Header with Real-time Status */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recruiter Dashboard</h1>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Last Updated */}
          {lastUpdated && (
            <div className="text-sm text-gray-600">
              <Clock className="w-4 h-4 inline mr-1" />
              Updated {lastUpdated.toLocaleTimeString()}
            </div>
          )}
          
          {/* Manual Refresh Button */}
          <button
            onClick={loadDashboardData}
            disabled={loading}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <TrendingUp className={`w-4 h-4 ${loading ? 'animate-pulse' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => navigate('/recruiter/positions')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all duration-200 group"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Briefcase className="h-8 w-8 text-blue-600 group-hover:text-blue-700" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate group-hover:text-gray-600">Active Positions</dt>
                <dd className="text-2xl font-bold text-gray-900 group-hover:text-blue-600">{dashboardStats.activePositions || 0}</dd>
              </dl>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-600 group-hover:text-gray-700">
            <TrendingUp className="inline w-4 h-4 mr-1" />
            {dashboardStats.topPositions?.length || 0} top performing
          </div>
          <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-blue-600 font-medium">
            Click to view all positions →
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onClick={() => navigate('/recruiter/candidates')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-green-300 transition-all duration-200 group"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-green-600 group-hover:text-green-700" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate group-hover:text-gray-600">Followed Candidates</dt>
                <dd className="text-2xl font-bold text-gray-900 group-hover:text-green-600">{followedCandidatesCount}</dd>
              </dl>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-600 group-hover:text-gray-700">
            <UserCheck className="inline w-4 h-4 mr-1" />
            Following {followedCandidatesCount} candidates
          </div>
          <div className="mt-1 text-xs text-gray-500 group-hover:text-gray-600">
            <TrendingUp className="inline w-3 h-3 mr-1" />
            +{dashboardStats.periodStats?.newApplications || 0} new applications this period
          </div>
          <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-green-600 font-medium">
            Click to view all candidates →
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onClick={() => navigate('/recruiter/interviews')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-purple-300 transition-all duration-200 group"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Calendar className="h-8 w-8 text-purple-600 group-hover:text-purple-700" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate group-hover:text-gray-600">Interviewed</dt>
                <dd className="text-2xl font-bold text-gray-900 group-hover:text-purple-600">{dashboardStats.totalInterviewed || 0}</dd>
              </dl>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-600 group-hover:text-gray-700">
            <Clock className="inline w-4 h-4 mr-1" />
            {dashboardStats.upcomingInterviews?.length || 0} upcoming interviews
          </div>
          <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-purple-600 font-medium">
            Click to view all interviews →
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          onClick={() => navigate('/recruiter/candidates?status=hired')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-orange-300 transition-all duration-200 group"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserCheck className="h-8 w-8 text-orange-600 group-hover:text-orange-700" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate group-hover:text-gray-600">Hired</dt>
                <dd className="text-2xl font-bold text-gray-900 group-hover:text-orange-600">{dashboardStats.totalHired || 0}</dd>
              </dl>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-600 group-hover:text-gray-700">
            <CheckCircle className="inline w-4 h-4 mr-1" />
            {dashboardStats.periodStats?.hiresMade || 0} hired this period
          </div>
          <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-orange-600 font-medium">
            Click to view hired candidates →
          </div>
        </motion.div>
      </div>

      {/* Dashboard Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left Side */}
        <div className="lg:col-span-2 space-y-6">
          {/* Activity Feed */}
          <ActivityFeed 
            activities={dashboardStats?.activityFeed || []} 
            loading={loading}
            onRefresh={loadDashboardData}
            showFilters={false}
          />
          
        </div>

        {/* Sidebar - Right Side */}
        <div className="space-y-6">
          {/* Favorite Positions */}
          <FavoritePositions 
            positions={positions.slice(0, 5)} 
            loading={loading}
          />
          
          {/* Urgent Actions */}
          {dashboardStats?.urgentActions && dashboardStats.urgentActions.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900">Urgent Actions</h3>
              </div>
              <div className="space-y-3">
                {dashboardStats.urgentActions.map((action: any, index: number) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-red-900">{action.title}</h4>
                      <p className="text-sm text-red-700 mt-1">{action.message}</p>
                      {action.actionUrl && (
                        <a 
                          href={action.actionUrl} 
                          className="inline-flex items-center mt-2 text-sm font-medium text-red-700 hover:text-red-900"
                        >
                          {action.actionText}
                          <ArrowRight className="w-3 h-3 ml-1" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Upcoming Interviews */}
          {dashboardStats?.upcomingInterviews && dashboardStats.upcomingInterviews.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Calendar className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Upcoming Interviews</h3>
              </div>
              <div className="space-y-3">
                {dashboardStats.upcomingInterviews.slice(0, 3).map((interview: any) => (
                  <div key={interview.id} className="flex items-start space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <Calendar className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-blue-900">{interview.title}</h4>
                      <p className="text-sm text-blue-700">{interview.candidate}</p>
                      <p className="text-xs text-blue-600 mt-1">
                        {interview.scheduledAt ? new Date(interview.scheduledAt).toLocaleString() : 'Time TBD'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              {dashboardStats.upcomingInterviews.length > 3 && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <Link 
                    to="/recruiter/interviews" 
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    View all {dashboardStats.upcomingInterviews.length} interviews →
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>


      {/* Modals */}
      <InterviewScheduleModal 
        open={showInterviewModal} 
        onClose={() => {
          setShowInterviewModal(false)
          setSelectedCandidateId(undefined)
          setSelectedPositionId(undefined)
        }} 
        candidateId={selectedCandidateId}
        positionId={selectedPositionId}
      />
    </div>
  )
}
